import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { IonNav, ModalController, ViewWillEnter } from '@ionic/angular';
import { PanAppState, PlatformService, jwtDecode } from '@panamax/app-state';
import { PopoverHelperService } from '@shared/helpers/popover.helpers';
import { Order, OrderHeader, OrderItem } from '@usf/ngrx-order';
import { debounceTime, filter, take, map, switchMap } from 'rxjs/operators';
import { ModalService } from '@shared/services/modal/modal.service';
import { TranslateService } from '@ngx-translate/core';
import { DatePipe } from '@angular/common';
import { FormBuilder, FormGroup } from '@angular/forms';
import { OrderService } from '@order/services/order.service';
import { AddToOrderViewModel } from '@order/models/add-to-order-view-model';
import { Subscription, Observable, of } from 'rxjs';
import moment from 'moment';
import { UserService } from '@app/user/services';
import { UserPreferencesV2 } from '@usf/user-types/user-preferencev2';
import { FEATURES } from '@shared/constants/splitio-features';

@Component({
  template: '',
})
export class AddToOrderBaseComponent
  implements OnInit, ViewWillEnter, OnDestroy
{
  @Input() orderHeader: OrderHeader;
  @Input() nextDeliveryDate: string | Date;
  @Input() productNumber: number;
  @Input() updateOrderItem: OrderItem;
  @Input() origOrderItems: OrderItem[];
  @Input() disablePO: boolean;

  currentTab = 1;
  isCustomerRequirePO = false;
  selectedDeliveryDate: any;
  vm: AddToOrderViewModel[];
  selectedOrderDetails: AddToOrderViewModel;
  deliveryDaysSub: Subscription;
  eligibleDeliveryDays: number[];
  prontoDeliveryDays: number[];
  featureFlagSub$: Subscription;
  defaultPoFeatureFlag = FEATURES.split_dynamic_default_po.name;
  isPoDisabledForVPTAndPunchOut = false;

  constructor(
    public nav: IonNav,
    protected datePipe: DatePipe,
    protected modalController: ModalController,
    protected modalService: ModalService,
    protected orderService: OrderService,
    protected popoverHelperService: PopoverHelperService,
    protected translate: TranslateService,
    readonly platformService: PlatformService,
    readonly panAppState: PanAppState,
    protected fb: FormBuilder,
    protected userService: UserService,
  ) {
    this.deliveryDaysSub = this.orderService
      .selectedCustomerDeliveryDays()
      .pipe(take(1))
      .subscribe(selectedDays => {
        this.eligibleDeliveryDays = selectedDays;
      });
    this.orderService
      .selectedCustomerProntoDeliveryDays()
      .pipe(take(1))
      .subscribe(selectedDays => {
        this.prontoDeliveryDays = selectedDays;
      });
  }

  ionViewWillEnter(): void {
    this.isPORequire();
    this.validatePoForVPTAndPunchOutUser();
    this.orderService
      .loadAddToOrderViewModel$()
      .pipe(debounceTime(10), take(1))
      .subscribe(
        (addToOrderVM: AddToOrderViewModel[]) => (this.vm = addToOrderVM),
      );
  }

  ngOnInit(): void {
    this.isPORequire();
  }

  loadDefaultPoNumber(): Observable<string> {
    return this.panAppState.feature$([this.defaultPoFeatureFlag]).pipe(
      switchMap(shouldLoadDefaultPo => {
        if (!shouldLoadDefaultPo) {
          return of('');
        }
        return this.userService.defaultPoNumber$(
          this.orderHeader.customerNumber,
        );
      }),
      map(defaultPoNumber => defaultPoNumber || ''),
      take(1),
    );
  }

  async closeModal() {
    const modData = {
      orderHeader: this.orderHeader,
      origOrderItems: this.origOrderItems,
      updateOrderItem: this.updateOrderItem,
    };
    this.orderService.cancelAddToOrderModal(modData);
    await this.modalController.dismiss();
  }

  toggleTab(tab: number) {
    if (this.currentTab !== tab) {
      this.currentTab = tab !== 2 ? 1 : 2;
    }
  }

  isPORequire(): void {
    this.panAppState.accessToken$
      .pipe(
        filter(token => !!token),
        take(1),
      )
      .subscribe(token => {
        const decoded = jwtDecode(token);
        if (decoded) {
          this.isCustomerRequirePO =
            decoded['usf-claims'].requireCustomerPO === 'Y' ? true : false;
        }
      });
  }

  validatePoForVPTAndPunchOutUser() {
    this.panAppState.accessToken$
      .pipe(
        filter(token => !!token),
        take(1),
      )
      .subscribe(token => {
        const decoded = jwtDecode(token);
        if (decoded) {
          this.isPoDisabledForVPTAndPunchOut =
            decoded['usf-claims']?.punchoutSession ||
            decoded['usf-claims']?.punchthruSession;
        }
      });
  }

  onOrderSelected(selectedRadioItem: AddToOrderViewModel) {
    this.selectedOrderDetails = selectedRadioItem;
  }

  async saveDefaultPoNumber(poNumber: string): Promise<void> {
    const currentPreferences = await this.userService
      .getUserPreferencesV2()
      .pipe(take(1))
      .toPromise();
    const updatedPreferences: UserPreferencesV2 = {
      ...currentPreferences,
      poNumber: [
        ...(currentPreferences.poNumber || []).filter(
          po => po.customerNumber !== this.orderHeader.customerNumber,
        ),
        {
          customerNumber: this.orderHeader.customerNumber,
          defaultPo: poNumber,
        },
      ],
    };
    await this.userService
      .updateUserPreferencesV2(updatedPreferences)
      .toPromise();
  }

  async onSubmit(currentTab: number, addToOrderModalForm: FormGroup) {
    if (currentTab === 2) {
      //add new qty to existing order from radio list for the customer in context
      const selectedOrder: Order = {
        orderHeader: this.selectedOrderDetails?.orderHeader,
        orderItems: this.selectedOrderDetails.orderItems,
      };

      //create modal object before making an order as current to avoid total conflicts
      const updateExistingOrder = {
        currentTab: currentTab,
        orderHeader: selectedOrder?.orderHeader,
        origOrderItems: selectedOrder?.orderItems,
        updateOrderItem: this.updateOrderItem, //new incoming qty for a product
      };

      await this.orderService.makeOrderCurrentOrder(
        selectedOrder,
        this.selectedOrderDetails?.selectedOrderCustomer,
      );
      this.orderService.submitAddToOrderModal(updateExistingOrder);
    } else {
      //create a new order from incoming qty for the customer in context
      const selectedDeliveryDate = moment.utc(
        addToOrderModalForm?.get('selectedDeliveryDate').value,
        'YYYY-MM-DDTHH:mm:ss',
      );
      let poNumber = addToOrderModalForm?.get('poNumber').value;
      const submittedModalData = {
        currentTab: currentTab,
        orderHeader: {
          ...this.orderHeader,
          purchaseOrderNumber: poNumber,
          requestedDeliveryDate: selectedDeliveryDate.isValid()
            ? selectedDeliveryDate.toISOString()
            : null,
          confirmedDeliveryDate: selectedDeliveryDate.isValid()
            ? selectedDeliveryDate.toISOString()
            : null,
        },
        origOrderItems: this.origOrderItems,
        updateOrderItem: this.updateOrderItem,
      };

      // Save PO number in user preferences if toggle is activated
      if (addToOrderModalForm?.get('saveDefaultPo')?.value) {
        await this.saveDefaultPoNumber(poNumber);
      }

      this.orderService.submitAddToOrderModal(submittedModalData);
    }
  }

  trackItem(index: any, item: any) {
    return item ? index : undefined;
  }

  isEligibleDay = (dateString: string) => {
    const today = moment().startOf('day').format('YYYY-MM-DD');
    if (today === dateString) {
      return false;
    }
    const date = new Date(dateString);
    const utcDay = date.getUTCDay();
    //Date will be enabled if it is in the eligibleDays array.
    const isDateEnabled = this.eligibleDeliveryDays?.includes(utcDay);
    return isDateEnabled;
  };

  prontoDayStyle = isoString => {
    if (this.prontoDeliveryDays === undefined) return undefined;
    if (moment().isSameOrBefore(isoString) && this.isEligibleDay(isoString)) {
      const date = new Date(isoString);
      const utcDay = date.getUTCDay();
      if (this.prontoDeliveryDays?.includes(utcDay)) {
        return {
          textColor: 'var(--ion-color-gray-80)',
          backgroundColor: '#F3BA1633',
        };
      } else {
        return {
          textColor: 'var(--ion-color-gray-80)',
          backgroundColor: 'var(--ion-color-primary-green-20)',
        };
      }
    }
    return undefined;
  };

  ngOnDestroy() {
    if (this.deliveryDaysSub) {
      this.deliveryDaysSub.unsubscribe();
    }
    if (this.featureFlagSub$) {
      this.featureFlagSub$.unsubscribe();
    }
  }
}
